// Un selector con la anatomía de los menús de Atlas (ADR-2026-10-15-formato-y-calidad-del-catalogo-como-en-atlas):
// una pastilla con el rótulo en gris, el valor y un chevron, que abre un panel con una FILA por
// opción —nombre, una línea de para qué es, lo que cuesta a la derecha y el check redondo—, la
// misma de los menús de las pastillas del chat (`cuerpoDeFilas` en `PastillasDeContenido`).
// Reemplaza a los `<select>` nativos de la configuración del Catálogo, que no decían qué cambia
// cada opción ni cuánto cuesta.
//
// El panel va por portal y `fixed`, pegado debajo de la pastilla (arriba si abajo no entra): la
// configuración vive adentro de contenedores con overflow y un `absolute` quedaría recortado.
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface OpcionDelSelector<T extends string> {
  id: T;
  etiqueta: string;
  /** Para qué sirve, en una línea. */
  detalle?: string;
  /** Lo que cuesta, a la derecha del nombre. */
  costo?: string;
  /** Un dibujo a la izquierda del nombre (la proporción de un formato). */
  glifo?: (activo: boolean) => ReactNode;
}

const SEPARACION = 6;
const ANCHO_PANEL = 320;
const MARGEN = 12;

export function SelectorDeOpciones<T extends string>({
  rotulo,
  valor,
  opciones,
  elegido,
  onElegir,
  pie,
}: {
  rotulo: string;
  /** Lo que se lee en la pastilla cerrada. */
  valor: string;
  opciones: OpcionDelSelector<T>[];
  elegido: T;
  onElegir: (id: T) => void;
  /** Una línea al pie del panel (lo que cuesta el catálogo entero, por ejemplo). */
  pie?: ReactNode;
}) {
  const [abierto, setAbierto] = useState(false);
  const botonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number } | null>(null);

  const medir = () => {
    const b = botonRef.current?.getBoundingClientRect();
    if (!b) return;
    const left = Math.max(MARGEN, Math.min(b.left, window.innerWidth - ANCHO_PANEL - MARGEN));
    const alto = panelRef.current?.offsetHeight ?? 0;
    const abajo = window.innerHeight - b.bottom - SEPARACION - MARGEN;
    setPos(alto > abajo && b.top > abajo ? { left, bottom: window.innerHeight - b.top + SEPARACION } : { left, top: b.bottom + SEPARACION });
  };

  useLayoutEffect(() => {
    if (!abierto) {
      setPos(null);
      return;
    }
    medir();
    window.addEventListener('resize', medir);
    window.addEventListener('scroll', medir, true);
    return () => {
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [abierto]);
  // Con el panel montado ya se sabe su alto: se vuelve a medir para elegir el lado.
  const montado = pos !== null;
  useLayoutEffect(() => {
    if (montado) medir();
  }, [montado]);

  useEffect(() => {
    if (!abierto) return;
    const afuera = (e: MouseEvent) => {
      const t = e.target as Node;
      if (botonRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAbierto(false);
        botonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto]);

  return (
    <>
      <button
        ref={botonRef}
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        className={`inline-flex items-center gap-1.5 rounded-full py-1.5 pl-3 pr-2.5 text-[13px] font-semibold transition-colors ${
          abierto ? 'bg-[#E9EDFD]' : 'bg-[#F1F3F9] hover:bg-[#E6E9F2]'
        }`}
      >
        <span className="font-medium text-[#3C3C43]/60">{rotulo}</span>
        <span className="text-brand-black">{valor}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-[#02062B]/[0.5] transition-transform duration-200 ${abierto ? 'rotate-180' : ''}`} strokeWidth={2.25} aria-hidden="true" />
      </button>
      {abierto &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={rotulo}
            style={{ position: 'fixed', left: pos?.left ?? -9999, top: pos?.top, bottom: pos?.bottom, width: ANCHO_PANEL, visibility: pos ? 'visible' : 'hidden' }}
            className="z-[300] animate-fade-in rounded-2xl bg-white p-2 shadow-[0_12px_40px_-12px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.06]"
          >
            <div role="radiogroup" aria-label={rotulo}>
              {opciones.map((o) => {
                const activo = o.id === elegido;
                return (
                  <button
                    key={o.id}
                    type="button"
                    role="radio"
                    aria-checked={activo}
                    onClick={() => {
                      onElegir(o.id);
                      setAbierto(false);
                    }}
                    className={`flex w-full items-start gap-3 rounded-xl py-2 px-3 text-left transition-colors ${activo ? 'bg-[#E9EDFD]' : 'hover:bg-[#02062B]/[0.04]'}`}
                  >
                    {o.glifo && <span className="mt-[3px] grid w-4 shrink-0 place-items-center">{o.glifo(activo)}</span>}
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className={`min-w-0 truncate text-[13.5px] ${activo ? 'font-semibold text-brand-primary' : 'font-medium text-brand-black'}`}>{o.etiqueta}</span>
                        {o.costo && <span className="shrink-0 text-[11.5px] text-[#02062B]/[0.5]">{o.costo}</span>}
                      </span>
                      {o.detalle && <span className="mt-0.5 block text-[11.5px] leading-snug text-[#02062B]/[0.5]">{o.detalle}</span>}
                    </span>
                    {/* Como en los menús del chat: la marca no es sólo color. */}
                    <span
                      aria-hidden="true"
                      className={`mt-0.5 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors ${
                        activo ? 'border-brand-primary bg-brand-primary text-white' : 'border-[#02062B]/[0.18] bg-white'
                      }`}
                    >
                      {activo && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })}
            </div>
            {pie && <p className="mt-1 border-t border-black/[0.06] px-3 pt-2 pb-1 text-[11.5px] leading-snug text-[#02062B]/[0.5]">{pie}</p>}
          </div>,
          document.body,
        )}
    </>
  );
}
